import React, { useState, useEffect } from "react";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import AddIcon from "@mui/icons-material/Add";
import pb from "../lib/pb";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";

export default function Match({ onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [index, setIndex] = useState(0);

  const [ownerTasks, setOwnerTasks] = useState([]);
  const [activeTask, setActiveTask] = useState(null);

  const [raiseOpen, setRaiseOpen] = useState(false);

  const [myTaskSets, setMyTaskSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);

  const [rightTaskSets, setRightTaskSets] = useState([]);
  const [rightSelectedSet, setRightSelectedSet] = useState(null);

  // ✅ NEW
  const [matchSession, setMatchSession] = useState(null);

  const currentUser = users[index];

  /* ================================
     LOAD OTHER USERS
  ================================= */
  useEffect(() => {
    pb.collection("users")
      .getFullList({ filter: `id != "${user.id}"` })
      .then(setUsers);
  }, []);

  /* ================================
     LOAD MY TASK SETS
  ================================= */
  const loadMyTaskSets = async () => {
    const sets = await pb.collection("task_sets").getFullList({
      filter: `owner="${user.id}"`
    });
    setMyTaskSets(sets.items);
  };

  useEffect(() => {
    loadMyTaskSets();

    pb.collection("task_sets").subscribe("*", e => {
      if (e.record.owner === user.id) loadMyTaskSets();
    });

    return () => pb.collection("task_sets").unsubscribe();
  }, []);

  /* ================================
     LOAD MATCH SESSION (Realtime)
  ================================= */
  useEffect(() => {
    if (!selectedSet || !currentUser) return;

    const loadSession = async () => {
      const session = await pb.collection("match_sessions")
        .getFirstListItem(
          `taskset="${selectedSet.id}" && owner="${currentUser.id}" && target="${user.id}"`
        )
        .catch(() => null);

      if (session) setMatchSession(session);
    };

    loadSession();

    pb.collection("match_sessions").subscribe("*", e => {
      if (
        e.record.taskset === selectedSet?.id &&
        (e.record.owner === currentUser?.id ||
          e.record.target === currentUser?.id)
      ) {
        setMatchSession(e.record);
      }
    });

    return () => pb.collection("match_sessions").unsubscribe();
  }, [selectedSet, currentUser]);

  /* ================================
     LOAD OWNER TASKS
  ================================= */
  const loadOwnerTasks = async (setId) => {
    if (!setId) {
      setOwnerTasks([]);
      return;
    }

    const list = await pb.collection("user_tasks").getFullList({
      filter: `user="${user.id}" && task_set="${setId}"`,
      sort: "index"
    });

    setOwnerTasks(list);

    // ✅ UPDATE PROGRESS
    if (matchSession) {
      const doneCount = list.filter(t => t.done).length;

      await pb.collection("match_sessions").update(matchSession.id, {
        progress: doneCount,
        unlocked: doneCount === 10,
        completed: doneCount === 10
      });
    }
  };

  useEffect(() => {
    if (!selectedSet) return;

    loadOwnerTasks(selectedSet.id);

    pb.collection("user_tasks").subscribe("*", e => {
      if (
        e.record.user === user.id &&
        e.record.task_set === selectedSet.id
      ) {
        loadOwnerTasks(selectedSet.id);
      }
    });

    return () => pb.collection("user_tasks").unsubscribe();
  }, [selectedSet, matchSession]);

  /* ================================
     LOAD OTHER USER TASKSETS
  ================================= */
  useEffect(() => {
    if (!currentUser) return;

    const loadRightSets = async () => {
      const sets = await pb.collection("task_sets").getFullList({
        filter: `owner="${currentUser.id}" && published=true`
      });
      setRightTaskSets(sets);
    };

    loadRightSets();
    setRightSelectedSet(null);

    pb.collection("task_sets").subscribe("*", e => {
      if (
        e.record.owner === currentUser.id &&
        e.record.published
      ) {
        loadRightSets();
      }
    });

    return () => pb.collection("task_sets").unsubscribe();
  }, [currentUser]);

  /* ================================
     START MATCH
  ================================= */
  const startMatch = async () => {
    if (!rightSelectedSet || !currentUser) {
      alert("Select a task set first");
      return;
    }

    const templates = await pb.collection("task_templates")
      .getFullList({
        filter: `task_set="${rightSelectedSet.id}"`,
        sort: "index"
      });

    if (templates.length !== 10) {
      alert("This task set is incomplete");
      return;
    }

    // ✅ CREATE MATCH SESSION
    const existing = await pb.collection("match_sessions")
      .getFirstListItem(
        `taskset="${rightSelectedSet.id}" && owner="${currentUser.id}" && target="${user.id}"`
      )
      .catch(() => null);

    let session;

    if (!existing) {
      session = await pb.collection("match_sessions").create({
        owner: currentUser.id,
        target: user.id,
        taskset: rightSelectedSet.id,
        progress: 0,
        unlocked: false,
        active: true,
        completed: false
      });
    } else {
      session = existing;
    }

    setMatchSession(session);

    // COPY TASKS (unchanged)
    const old = await pb.collection("user_tasks")
      .getFullList({
        filter: `user="${user.id}" && task_set="${rightSelectedSet.id}"`
      });

    for (const o of old) {
      await pb.collection("user_tasks").delete(o.id);
    }

    for (let i = 0; i < templates.length; i++) {
      await pb.collection("user_tasks").create({
        user: user.id,
        match_user: currentUser.id,
        task_set: rightSelectedSet.id,
        index: i,
        title: templates[i].title,
        desc: templates[i].desc,
        enabled: i === 0,
        done: false
      });
    }

    setSelectedSet(rightSelectedSet);
    loadOwnerTasks(rightSelectedSet.id);

    alert("Match Started 🚀");
  };

  /* ================================
     PROGRESS MESSAGES
  ================================= */
  const messages = [
    "A spark has begun ✨",
    "Energy is building 🔥",
    "You're syncing 🌊",
    "Understanding grows 🌿",
    "Connection forming 💫",
    "You're getting closer ❤️",
    "Trust is rising 🤝",
    "Emotions aligning 💞",
    "Almost there… 💓"
  ];

  return (
    <div className="match-overlay">
      <div className="match-modal">
        <button className="match-close" onClick={onClose}>✖</button>

        {/* LEFT SIDE */}
        <div className="match-left">
          <h3>Your Tasks</h3>

          {/* ✅ PROGRESS BAR */}
          {matchSession && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                {[...Array(10)].map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: "50%",
                      background:
                        i < (matchSession.progress || 0)
                          ? "#ff4d6d"
                          : "#ddd"
                    }}
                  />
                ))}
              </div>

              {matchSession.progress > 0 &&
                matchSession.progress < 10 && (
                  <p style={{ marginTop: 10, fontWeight: "bold" }}>
                    {messages[matchSession.progress - 1]}
                  </p>
                )}

              {matchSession.progress === 10 && (
                <>
                  <p style={{ color: "green", fontWeight: "bold" }}>
                    💖 Connection Unlocked!
                  </p>

                  <button
                    onClick={() => navigate(`/chat/${currentUser.id}`)}
                    style={{
                      marginTop: 10,
                      padding: "8px 16px",
                      background: "#ff4d6d",
                      color: "#fff",
                      border: "none",
                      borderRadius: 6
                    }}
                  >
                    Start Chat 💬
                  </button>
                </>
              )}
            </div>
          )}

          {ownerTasks.map((t, i) => (
  <div
    key={t.id}
    className={`owner-task ${t.enabled ? "enabled" : ""} ${t.done ? "done" : ""}`}
    style={{
      display: "flex",
      alignItems: "center",
      gap: 10,
      opacity: t.enabled ? 1 : 0.5
    }}
  >
    <input
      type="checkbox"
      disabled={!t.enabled}
      checked={t.done}
      onChange={async () => {

        if (!t.enabled) return;

        // mark done
        await pb.collection("user_tasks").update(t.id, {
          done: !t.done
        });

        // enable next task
        if (!t.done && ownerTasks[i + 1]) {
          await pb.collection("user_tasks").update(
            ownerTasks[i + 1].id,
            { enabled: true }
          );
        }

      }}
    />

    <span>{t.title}</span>
  </div>
))}


          <div className="you-container">
            <img
              src={pb.files.getURL(user, user.avatar)}
              className="you-dp"
              alt=""
            />
            <div className="you-label">You</div>

            <button
              className="raise-btn"
              onClick={() => setRaiseOpen(true)}
            >
              <AddIcon /> Raise Task
            </button>
          </div>

          <div className="my-tasksets">
            {myTaskSets.map(set => (
              <div
                key={set.id}
                className={`taskset-card ${selectedSet?.id === set.id ? "selected" : ""}`}
                onClick={() => {
                  setSelectedSet(set);
                  loadOwnerTasks(set.id);
                }}
              >
                <img src={pb.files.getURL(set, set.cover)} alt="" />
                <h4>{set.title}</h4>
                <p>{set.headline}</p>
                <span>{set.published ? "Published" : "Draft"}</span>
              </div>
            ))}
          </div>
        </div>

        {/* RIGHT SIDE — UNCHANGED */}
        <div className="match-right">
          {currentUser && (
            <>
              <h3>{currentUser.username}'s Task Sets</h3>

              <div className="user-card">
                <img
                  src={pb.files.getURL(currentUser, currentUser.avatar)}
                  alt=""
                />
                <span>{currentUser.username}</span>
              </div>

              <div className="my-tasksets">
                {rightTaskSets.map(set => (
                  <div
                    key={set.id}
                    className={`taskset-card ${rightSelectedSet?.id === set.id ? "selected" : ""}`}
                    onClick={() => setRightSelectedSet(set)}
                  >
                    <img src={pb.files.getURL(set, set.cover)} alt="" />
                    <h4>{set.title}</h4>
                    <p>{set.headline}</p>
                  </div>
                ))}
              </div>

              <div className="match-controls">
                <ArrowBackIosNewIcon
                  onClick={() =>
                    setIndex((index - 1 + users.length) % users.length)
                  }
                />

                <button className="start-btn" onClick={startMatch}>
                  Start
                </button>

                <ArrowForwardIosIcon
                  onClick={() =>
                    setIndex((index + 1) % users.length)
                  }
                />
              </div>
            </>
          )}
        </div>
      </div>

      {raiseOpen && (
        <RaiseTaskModal
          user={user}
          onClose={() => {
            setRaiseOpen(false);
            loadMyTaskSets();
          }}
        />
      )}
    </div>
  );
}
